import { dateRangePopperStyle } from '~/components/DateTimePicker/DateTimePicker.styled';
import FormError from '~/components/FormError';
import Input from '~/components/Input';
import { LabelProps } from '~/components/Label/Label';
import { Keys } from '~/constants/keys';
import * as moment from 'moment';
import * as React from 'react';
import { useCallback } from 'react';
import DatePicker from 'react-datepicker';
import { WrappedFieldProps } from 'redux-form';

interface DateTimePickerProps {
  onChange: (date: string | moment.Moment) => void;
  value?: moment.Moment | Date;
  id?: string;
  name?: string;
  label?: string;
  labelProps?: LabelProps;
  hasTime?: boolean;
  disabled?: boolean;
  disablePastDates?: boolean;
  disableWeekends?: boolean;
  minDate?: Date | moment.Moment;
  dateLimit?: Date | moment.Moment;
  tabIndex?: number;
  invalid?: boolean;
  dataAnalytics?: string;
  dataPendo?: string;
  dataTestid?: string;
  errorMessage?: string;
  input?: JSX.Element;
  // Applies style classes to inner input element
  className?: string;
  placeholder?: string;
  wrapperClassName?: string;
}

interface DateRangePickerProps
  extends Omit<DateTimePickerProps, 'onChange' | 'value' | 'dataTestid'> {
  value: [Date | undefined, Date | undefined];
  onChange: (dates: [Date | null, Date | null] | null) => void;
}

const DateTimePickerInput = (props: DateTimePickerProps | DateRangePickerProps) => {
  return (
    <Input
      {...props}
      readOnly
      autoComplete="off"
      errorMessage={undefined} // Do not set errorMessage prop for Input since error message rendering is handled in DateTimePicker
      onChange={undefined} // Do not set onChange since the text box (Input) should be read only
      value={props.value?.toString()}
    />
  );
};

const DateTimePicker = (props: DateTimePickerProps) => {
  const isValidDate = React.useCallback(
    (date: Date) => {
      const current = moment(date);
      if (props.disableWeekends && (current.day() === 0 || current.day() === 6)) return false;
      return true;
    },
    [props.disableWeekends],
  );

  const renderErrors = React.useCallback(() => {
    if (props.errorMessage) {
      return <FormError>{props.errorMessage}</FormError>;
    }
  }, [props.errorMessage]);

  // Basically a dummy onChange to keep backwards compat with usages that rely on moment functions
  const onChange = React.useCallback(
    (date: Date) => {
      const momentDate = moment(date);
      return props.onChange(momentDate);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onChange],
  );

  // This check is because the UPS carrier form is passing an invalid moment date
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isValidValue = React.useMemo(() => moment(props.value).isValid(), [props.value]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const momentDate = React.useMemo(
    () => (isValidValue ? moment(props.value) : moment()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.value],
  );

  const onKeyDown = useCallback((e) => {
    if (e.key === Keys.Tab) {
      e.stopPropagation();
    }
  }, []);

  return (
    <div>
      <DatePicker
        id={props.id}
        onKeyDown={onKeyDown}
        wrapperClassName={props.wrapperClassName}
        showTimeSelect={props.hasTime}
        minDate={
          props.disablePastDates
            ? new Date()
            : props.minDate
              ? moment(props.minDate).toDate()
              : null
        }
        maxDate={props.dateLimit ? moment(props.dateLimit).toDate() : null}
        onChange={onChange}
        selected={isValidValue ? momentDate.toDate() : undefined} // no selection if value is invalid
        disabled={props.disabled}
        customInput={props.input || <DateTimePickerInput {...props} />}
        filterDate={isValidDate}
        placeholderText={props.placeholder}
        popperClassName={dateRangePopperStyle as unknown as string}
        className={props.className}
        autoComplete="off"
      />
      {renderErrors()}
    </div>
  );
};

const DateRangePicker = (props: DateRangePickerProps) => {
  const isValidDate = React.useCallback(
    (date: Date) => {
      const current = moment(date);
      if (props.disableWeekends && (current.day() === 0 || current.day() === 6)) return false;
      return true;
    },
    [props.disableWeekends],
  );

  const formatWeekDay = React.useCallback((name) => name.substr(0, 1), []);

  const renderErrors = () => {
    if (props.errorMessage) {
      return <FormError>{props.errorMessage}</FormError>;
    }
  };

  // Basically a dummy onChange to keep backwards compat with usages that rely on moment functions
  const onChange = React.useCallback(
    (dates) => {
      return props.onChange(dates);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onChange],
  );

  const [startDate, endDate] = props.value;
  const onKeyDown = useCallback((e) => {
    if (e.key === Keys.Tab) {
      e.stopPropagation();
    }
  }, []);
  return (
    <>
      <DatePicker
        id={props.id}
        onKeyDown={onKeyDown}
        wrapperClassName={props.wrapperClassName}
        showTimeSelect={props.hasTime}
        disabled={props.disabled}
        minDate={
          props.disablePastDates
            ? new Date()
            : props.minDate
              ? moment(props.minDate).toDate()
              : null
        }
        maxDate={props.dateLimit ? moment(props.dateLimit).toDate() : null}
        onChange={onChange}
        selected={startDate}
        customInput={props.input || <DateTimePickerInput {...props} />}
        filterDate={isValidDate}
        formatWeekDay={formatWeekDay}
        placeholderText={props.placeholder}
        className={props.className}
        popperClassName={dateRangePopperStyle as unknown as string}
        selectsRange={true}
        startDate={startDate}
        endDate={endDate}
      />
      {renderErrors()}
    </>
  );
};

const DateTimePickerField = (props: Partial<DateTimePickerProps> & WrappedFieldProps) => {
  const { input, meta } = props;
  const invalid = meta && meta.touched && meta.error;
  const errorMessage = invalid ? meta.error : null;

  const requiredDatePickerProperties = {
    errorMessage,
    id: props.id,
    name: props.name,
    label: props.label,
    labelProps: props.labelProps,
    disablePastDates: props.disablePastDates,
    disableWeekends: props.disableWeekends,
    dateLimit: props.dateLimit,
  };

  // Keep the store serializable.
  const date = React.useMemo(() => moment(input.value), [input.value]);
  const onDateChange = React.useCallback(
    (date: string | moment.Moment | Date) => {
      input.onChange(date.toString());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [input.onChange],
  );

  return (
    <DateTimePicker
      value={date}
      onChange={onDateChange}
      {...requiredDatePickerProperties}
      tabIndex={props.tabIndex}
      dataAnalytics={props.dataAnalytics}
      dataPendo={props.dataPendo}
      dataTestid={props.dataTestid}
      invalid={invalid}
    />
  );
};

export default DateTimePicker;
export { DateRangePicker, DateTimePickerField };
